/**
 * Forest wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const forestWallpapersPack: ToilePack = {
    slug: 'forest-wallpapers',
    category: 'screens',
    name: 'Forest wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'fog', label: 'Foggy pines', subject: 'tall dark pine trees fading into thick grey fog, the trunks straight and the tops lost in the mist'},
        {key: 'sunbeams', label: 'Mossy forest', subject: 'sunbeams falling through a mossy green forest, ferns on the ground and a fallen log covered in moss'},
        {key: 'enchanted', label: 'Enchanted forest', subject: 'an enchanted forest at dusk with glowing mushrooms at the foot of old trees and fireflies floating in the dark'},
    ],
    styles: [
        {key: 'moody', label: 'Moody photo', style: 'dark moody forest photograph look, fog layers, muted tones, quiet atmosphere', palette: 'deep green, grey and charcoal', framing: 'Full-bleed, trunks rising through the lower two thirds'},
        {key: 'glow', label: 'Fantasy glow', style: 'painterly fantasy art, volumetric light, drifting particles, rich saturated greens', palette: 'emerald, teal and gold glow', framing: 'Full-bleed, the scene in the lower two thirds with a dim canopy above'},
        {key: 'minimal', label: 'Flat trees', style: 'minimal flat illustration of layered trees, smooth gradients and soft fog bands', palette: 'sage, pine and mist grey', framing: 'Full-bleed, tree layers stacked up the screen with fog calm at the top'},
    ],
    directions: ['rain forest', 'autumn forest', 'tree house'],
    directionHint: 'try: rain forest',
    seo: {
        title: 'Forest Wallpapers: Foggy Pines, Mossy Woods, Enchanted',
        metaDescription: 'Forest phone wallpapers, green and quiet: tall pines in fog, a mossy woodland with sunbeams and an enchanted forest, in three styles.',
        h1: 'Forest wallpapers: foggy pines, a mossy forest, an enchanted forest',
        tagline: 'Nine calm and moody phone wallpapers, three forests in three styles.',
        intro: 'Phone wallpapers of forests: tall dark pines fading into grey fog, sunbeams through a mossy green wood, and an enchanted forest at dusk with mushrooms and fireflies. Each comes as a moody forest photo look, a painted fantasy glow and a flat layered-tree illustration. People who want a calm or moody screen can set them as they are. Type a different scene, such as a rain forest, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a calm desktop background',
            'a reading-nook or bedroom print',
            'a gift for a nature lover',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three forest scenes: pines in fog, sunbeams through a mossy wood, and an enchanted forest with mushrooms and fireflies. Each scene is shown in three styles.'},
            {q: 'Can I change the forest?', a: 'Yes. Type a scene such as a rain forest, an autumn forest or a tree house; a typed scene is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Moody photo has fog layers and muted tones. Fantasy glow is painterly, with light and drifting particles. Flat trees is a minimal illustration of layered trees with soft fog bands.'},
            {q: 'Do the pictures have any text?', a: 'No. There is no text or lettering, and the upper part of each picture is kept calm for the clock.'},
        ],
    },
}
